import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as LinkDeRota, useNavigate } from 'react-router-dom';

import { ErroDaApi } from '../../shared/api/erros';
import { opcoesDosContratosParaEscolha } from '../../shared/api/contratosParaEscolha';
import { formatarCompetencia } from '../../shared/dominio/datas';
import { caminhoDaConferenciaDeFaturamento } from '../../shared/navegacao/mapaDeNavegacao';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { opcoesDasConferencias } from './api/faturamento';
import { ImportarConferencia } from './ImportarConferencia';
import { SeloDeEstadoDaConferencia } from './SeloDeEstadoDaConferencia';

/** `''` = "todos os contratos", o mesmo filtro que `GET /faturamento/conferencias` aceita sem `contratoId`. */
const TODOS_OS_CONTRATOS = '';

/**
 * O gate de faturamento (`TP-052`, T9, `S12`): a conta fechada do lote,
 * importada por uma pessoa, conferida contra as regras vigentes. Uma linha
 * `BARRADO` segura o lote até decisão humana (`DetalheDaConferencia.tsx`).
 *
 * Nenhum botão aqui ou no detalhe envia nada ao IW ou à operadora — o envio
 * é o `E9-07`, fora de escopo desta fase (restrição 9, `D5`/`D7` do
 * `BR-008`).
 */
export function PaginaDeFaturamento(): React.JSX.Element {
  const navegar = useNavigate();
  const consultaDosContratos = useQuery(opcoesDosContratosParaEscolha());
  const [contratoEscolhidoId, definirContratoEscolhidoId] = useState(TODOS_OS_CONTRATOS);

  const contratos = useMemo(() => consultaDosContratos.data ?? [], [consultaDosContratos.data]);

  useEffect(() => {
    if (consultaDosContratos.error instanceof ErroDaApi && consultaDosContratos.error.status === 401) {
      void navegar('/entrar', { replace: true });
    }
  }, [consultaDosContratos.error, navegar]);

  const consultaDasConferencias = useQuery(opcoesDasConferencias(contratoEscolhidoId));

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Faturamento</Typography>
        <Typography variant="body2" color="text.secondary">
          O lote fechado pelo IW entra pela importação do XML TISS, abaixo. O gate confere cada
          guia contra as regras e os itens vigentes na competência — uma linha <strong>Barrado</strong>{' '}
          segura o lote inteiro até uma pessoa decidir. Nada é enviado ao IW nem à operadora por
          esta tela: o envio é um passo futuro, sempre com um clique humano em separado.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            select
            label="Contrato"
            size="small"
            value={contratoEscolhidoId}
            onChange={(evento) => {
              definirContratoEscolhidoId(evento.target.value);
            }}
            sx={{ maxWidth: 360 }}
          >
            <MenuItem value={TODOS_OS_CONTRATOS}>Todos os contratos</MenuItem>
            {contratos.map((contrato) => (
              <MenuItem key={contrato.id} value={contrato.id}>
                {contrato.rotulo}
              </MenuItem>
            ))}
          </TextField>

          <ImportarConferencia contratoId={contratoEscolhidoId} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h2" sx={{ mb: 1 }}>
          Conferências
        </Typography>

        {consultaDasConferencias.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

        {consultaDasConferencias.isError ? (
          <EstadoDeErro
            erro={consultaDasConferencias.error}
            contexto="Não foi possível carregar as conferências"
            aoReprocessar={() => {
              void consultaDasConferencias.refetch();
            }}
          />
        ) : null}

        {consultaDasConferencias.isSuccess && consultaDasConferencias.data.length === 0 ? (
          <EstadoVazio
            titulo="Nenhuma conferência de faturamento"
            descricao="Nenhum lote foi importado ainda para este filtro."
          />
        ) : null}

        {consultaDasConferencias.isSuccess && consultaDasConferencias.data.length > 0 ? (
          <TableContainer>
            <Table aria-label="Conferências de faturamento" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Competência</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">OK</TableCell>
                  <TableCell align="right">Alerta</TableCell>
                  <TableCell align="right">Barrado</TableCell>
                  <TableCell align="right">Sem cobertura</TableCell>
                  <TableCell>Motivo da recusa</TableCell>
                  <TableCell>Detalhe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consultaDasConferencias.data.map((conferencia) => (
                  <TableRow hover key={conferencia.id}>
                    <TableCell>{formatarCompetencia(conferencia.competencia)}</TableCell>
                    <TableCell>
                      <SeloDeEstadoDaConferencia estado={conferencia.estado} />
                    </TableCell>
                    <TableCell align="right">{conferencia.ok}</TableCell>
                    <TableCell align="right">{conferencia.alerta}</TableCell>
                    <TableCell align="right">{conferencia.barrado}</TableCell>
                    <TableCell align="right">{conferencia.semCobertura}</TableCell>
                    <TableCell>{conferencia.motivoRecusa ?? '—'}</TableCell>
                    <TableCell>
                      <Button size="small" component={LinkDeRota} to={caminhoDaConferenciaDeFaturamento(conferencia.id)}>
                        Abrir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Paper>
    </Stack>
  );
}
